import { type FC, useState, useMemo } from 'react';
import type { Order, OrderItem, OrderItemHistory, CakeOrder } from '../../types';
import { useToast } from '../../hooks/useToast';
import { useOrderDetail } from '../../hooks/useOrderDetail';
import EditItemModal from './EditItemModal';
import DeleteOrderItemModal from './DeleteOrderItemModal';
import { CakeIcon, CalendarIcon, ShoppingBagIcon, ScaleIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface OrderDetailsTabProps {
    currentOrder: Order;
    items: OrderItem[];
    itemHistory: OrderItemHistory[];
    cakeOrder?: CakeOrder | null;
    onRefresh: () => Promise<void>;
}

// Summarise the audit trail for a single item into a human-readable label.
// Returns null if nothing noteworthy happened (i.e. quantity was never changed).
function buildQtyChangeLabel(itemId: string, history: OrderItemHistory[]): string | null {
    const qtyChanges = history.filter(h => h.item_id === itemId && h.column === 'quantity');
    if (qtyChanges.length === 0) return null;

    // The first entry's `previous` is the original quantity; the last entry's
    // `current` is the final quantity after all edits.
    const original = Number(qtyChanges[0].previous);
    const final    = Number(qtyChanges[qtyChanges.length - 1].current);

    if (original === final) return null;
    return String(original);
}

// Describes a picking shortfall for an item — i.e. fewer units were scanned
// at picking than the order required (out of stock, damaged, etc). Returns
// null when the item was fully picked, hasn't reached picking yet, has no
// picking record, or is a deli/variable-weight line (those are billed via
// actual_price_cents directly and always have quantity 1, so a picking
// shortfall doesn't apply to them the same way).
function buildPickingShortfall(item: OrderItem, isDeli: boolean): { label: string; tone: 'red' | 'amber' } | null {
    if (isDeli) return null;
    if (item.picking_status !== 'PARTIAL' && item.picking_status !== 'MISSING') return null;

    const required = item.picking_quantity_required ?? item.quantity;
    const picked   = item.picking_quantity_picked ?? 0;

    return {
        label: item.picking_status === 'MISSING' ? 'Not picked — out of stock' : `${picked} of ${required} picked`,
        tone:  item.picking_status === 'MISSING' ? 'red' : 'amber',
    };
}

const OrderDetailsTab: FC<OrderDetailsTabProps> = ({
                                                       currentOrder,
                                                       items: initialItems,
                                                       itemHistory: initialHistory,
                                                       cakeOrder = null,
                                                       onRefresh,
                                                   }) => {
    const { success, error } = useToast();
    const { deleteItem, editItem } = useOrderDetail();

    const [items,             setItems]             = useState<OrderItem[]>(initialItems);
    const [history]                                 = useState<OrderItemHistory[]>(initialHistory);
    const [isModalOpen,       setIsModalOpen]       = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedItem,      setSelectedItem]      = useState<OrderItem | null>(null);
    const [deleteReason,      setDeleteReason]      = useState('');
    const [isSubmitting,      setIsSubmitting]      = useState(false);
    const [cakeExpanded,      setCakeExpanded]      = useState(false);

    const fmt = (amount: number | string) => `R ${(Number(amount) / 100).toFixed(2)}`;

    const status        = currentOrder.status?.toUpperCase();
    const isOrderLocked = ['DELIVERY_COMPLETE', 'ORDER_CANCELLED', 'ON_ROUTE'].includes(status);

    const handleEditClick   = (item: OrderItem) => { setSelectedItem(item); setIsModalOpen(true); };
    const handleDeleteClick = (item: OrderItem) => { setSelectedItem(item); setDeleteReason(''); setIsDeleteModalOpen(true); };

    const handleDeleteFromEdit = (reason: string) => {
        setIsModalOpen(false);
        setDeleteReason(reason);
        setIsDeleteModalOpen(true);
    };

    const handleModalSave = async (previousQty: number, currentQty: number, reason: string) => {
        if (!selectedItem) return;
        setIsSubmitting(true);
        try {
            await editItem(String(currentOrder.id), selectedItem.id, previousQty, currentQty, reason);
            setItems(prev => prev.map(i =>
                i.id === selectedItem.id
                    ? { ...i, quantity: currentQty, total_price: Number(i.unit_price) * currentQty }
                    : i
            ));
            success(`${selectedItem.product_name} updated — audit trail logged`);
            setIsModalOpen(false);
            setSelectedItem(null);
            await onRefresh();
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to update item');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteConfirm = async (confirmReason: string) => {
        if (!selectedItem) return;
        setIsSubmitting(true);
        try {
            const finalReason = deleteReason || confirmReason;
            await deleteItem(String(currentOrder.id), selectedItem.id, finalReason);
            // Soft-delete: keep row visible as greyed-out ghost
            setItems(prev => prev.map(i =>
                i.id === selectedItem.id ? { ...i, deleted: true } : i
            ));
            success(`${selectedItem.product_name} deleted — audit trail logged`);
            setIsDeleteModalOpen(false);
            setSelectedItem(null);
            setDeleteReason('');
            await onRefresh();
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to delete item');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Active items contribute to the order total; deleted items do not.
    const activeItems = useMemo(() => items.filter(i => !i.deleted), [items]);
    const orderTotal  = useMemo(
        () => activeItems.reduce((sum, i) => sum + Number(i.total_price), 0),
        [activeItems]
    );

    const hasItems = items.length > 0;
    const hasNotes = !!currentOrder.notes?.trim();
    const hasCake  = !!cakeOrder;

    if (!hasItems && !hasNotes && !hasCake) {
        return (
            <div className="rounded-2xl bg-white border border-stone-200 shadow-sm p-12 text-center">
                <p className="text-sm text-stone-500">No products added to this order yet.</p>
            </div>
        );
    }

    return (
        <>
            {/* Notes */}
            {hasNotes && (
                <div className="mb-4 rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden">
                    <div className="bg-stone-50 border-b border-stone-200 px-6 py-3">
                        <h3 className="text-xs font-semibold text-stone-900 uppercase tracking-wide">Order Details / Notes</h3>
                    </div>
                    <div className="px-6 py-4">
                        <p className="whitespace-pre-wrap text-sm text-stone-900">{currentOrder.notes}</p>
                    </div>
                </div>
            )}

            {/* Regular line items */}
            {hasItems && (
                <div className="rounded-2xl bg-white border border-stone-200 shadow-sm overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                        <table className="w-full divide-y divide-stone-200">
                            <thead className="bg-stone-50">
                            <tr>
                                {['Product', 'SKU', 'Unit Price', 'Qty', 'Total', 'Actions'].map(h => (
                                    <th
                                        key={h}
                                        className={`px-6 py-3 text-xs font-semibold text-stone-900 uppercase tracking-wide ${
                                            h === 'Unit Price' || h === 'Qty' || h === 'Total'
                                                ? 'text-right'
                                                : h === 'Actions'
                                                    ? 'text-center print:hidden'
                                                    : 'text-left'
                                        }`}
                                    >
                                        {h}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-200">
                            {items.map(item => {
                                const isDeleted      = !!item.deleted;
                                const isDeli         = item.actual_price_cents != null;
                                const priceChanged   = isDeli && item.actual_price_cents !== item.estimated_unit_price;
                                const qtyChangeLabel = buildQtyChangeLabel(item.id, history);
                                const qtyReduced     = !isDeleted && qtyChangeLabel !== null;
                                const pickingShortfall = !isDeleted ? buildPickingShortfall(item, isDeli) : null;

                                return (
                                    <tr
                                        key={item.id}
                                        className={`transition ${isDeleted ? 'bg-stone-50' : 'hover:bg-stone-50'}`}
                                    >
                                        {/* Product */}
                                        <td className="px-6 py-4 text-sm">
                                            <div className="flex items-center gap-2">
                                                <span className={isDeleted ? 'line-through text-stone-400' : 'text-stone-900'}>
                                                    {item.product_name}
                                                </span>
                                                {!isDeleted && isDeli && (
                                                    <span title="Variable-weight item, scanned at picking">
                                                        <ScaleIcon className="h-3.5 w-3.5 text-[#ee2b74]" />
                                                    </span>
                                                )}
                                                {isDeleted && (
                                                    <span className="inline-flex items-center rounded-full bg-red-50 border border-red-200 px-2 py-0.5 text-[10px] font-semibold text-red-500 uppercase tracking-wide">
                                                        Removed
                                                    </span>
                                                )}
                                            </div>
                                            {!isDeleted && isDeli && item.actual_weight_kg != null && (
                                                <p className="text-xs text-stone-400 mt-0.5">
                                                    Scanned weight: {item.actual_weight_kg.toFixed(3)} kg
                                                </p>
                                            )}
                                        </td>

                                        {/* SKU */}
                                        <td className={`px-6 py-4 text-sm ${isDeleted ? 'text-stone-400' : 'text-stone-900'}`}>
                                            {item.sku}
                                        </td>

                                        {/* Unit price */}
                                        <td className={`px-6 py-4 text-right text-sm ${isDeleted ? 'text-stone-400' : 'text-stone-900'}`}>
                                            {!isDeleted && priceChanged ? (
                                                <div>
                                                    <p className="text-stone-400 line-through text-xs">{fmt(item.estimated_unit_price)}</p>
                                                    <p className="font-semibold text-[#ee2b74]">{fmt(item.unit_price)}</p>
                                                </div>
                                            ) : (
                                                fmt(item.unit_price)
                                            )}
                                        </td>

                                        {/* Qty — show reduction badge if quantity was lowered, and a
                                            picking-shortfall pill if fewer units were scanned than required */}
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex flex-col items-end gap-1">
                                                {isDeleted ? (
                                                    <span className="text-sm text-stone-400 line-through">
                                                        {item.quantity}
                                                    </span>
                                                ) : qtyReduced ? (
                                                    <span className="inline-flex items-center gap-1 text-sm whitespace-nowrap">
                                                        <span className="text-stone-400">{qtyChangeLabel}</span>
                                                        <span className="text-stone-400">→</span>
                                                        <span className="font-semibold text-stone-900">{item.quantity}</span>
                                                    </span>
                                                ) : (
                                                    <span className="text-sm font-medium text-stone-900">
                                                        {item.quantity}
                                                    </span>
                                                )}
                                                {pickingShortfall && (
                                                    <span
                                                        title={pickingShortfall.tone === 'red'
                                                            ? `${item.picking_quantity_required ?? item.quantity} ordered, none in stock`
                                                            : `${item.picking_quantity_picked ?? 0} of ${item.picking_quantity_required ?? item.quantity} in stock`}
                                                        className={`inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${
                                                            pickingShortfall.tone === 'red'
                                                                ? 'bg-red-50 text-red-700'
                                                                : 'bg-amber-50 text-amber-700'
                                                        }`}
                                                    >
                                                        <ExclamationTriangleIcon className="h-3 w-3 shrink-0" />
                                                        {pickingShortfall.label}
                                                    </span>
                                                )}
                                            </div>
                                        </td>

                                        {/* Total */}
                                        <td className={`px-6 py-4 text-right text-sm font-semibold ${isDeleted ? 'text-stone-400 line-through' : 'text-stone-900'}`}>
                                            {!isDeleted && priceChanged ? (
                                                <div>
                                                    <p className="text-stone-400 line-through text-xs font-normal">{fmt(item.estimated_unit_price * item.quantity)}</p>
                                                    <p className="text-[#ee2b74]">{fmt(item.total_price)}</p>
                                                </div>
                                            ) : !isDeleted && pickingShortfall ? (
                                                <div>
                                                    <p className="text-stone-400 line-through text-xs font-normal">{fmt(item.unit_price * item.quantity)}</p>
                                                    <p className={pickingShortfall.tone === 'red' ? 'text-red-600' : 'text-amber-600'}>
                                                        {fmt(item.total_price)}
                                                    </p>
                                                </div>
                                            ) : (
                                                fmt(item.total_price)
                                            )}
                                        </td>

                                        {/* Actions */}
                                        <td className="px-6 py-4 text-center print:hidden">
                                            {isDeleted ? (
                                                <span className="text-[11px] text-stone-400 italic">—</span>
                                            ) : (
                                                <div className="flex justify-center gap-2">
                                                    <button
                                                        onClick={() => handleEditClick(item)}
                                                        disabled={isSubmitting || isOrderLocked}
                                                        className="inline-flex items-center rounded px-2.5 py-1 text-xs font-medium bg-stone-100 text-stone-900 hover:bg-stone-200 disabled:opacity-50"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(item)}
                                                        disabled={isSubmitting || isOrderLocked}
                                                        className="inline-flex items-center rounded px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>

                    <div className="bg-stone-50 px-6 py-4 border-t border-stone-200">
                        <div className="flex justify-end">
                            <div>
                                <p className="text-xs text-stone-500 uppercase tracking-wide">Order Total</p>
                                <p className="mt-1 text-2xl font-bold text-stone-900">
                                    {fmt(orderTotal)}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cake order section */}
            {hasCake && (
                <div className="rounded-2xl border border-[#ee2b74]/25 bg-white shadow-sm overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setCakeExpanded(p => !p)}
                        className="w-full flex items-center gap-2 px-6 py-3 bg-pink-50/60 border-b border-[#ee2b74]/20 hover:bg-pink-50 transition-colors text-left"
                    >
                        <CakeIcon className="h-4 w-4 text-[#ee2b74]" />
                        <h3 className="text-xs font-semibold text-[#ee2b74] uppercase tracking-wide">Cake Order</h3>
                        {cakeOrder!.collected_at ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#f0f7e6] border border-[#c5e08a] px-2.5 py-0.5 text-xs font-semibold text-[#5a7f28]">
                                <CheckCircleIcon className="h-3 w-3" />
                                Collected
                            </span>
                        ) : (
                            <span className="rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                                Pending Collection
                            </span>
                        )}
                        <svg
                            className={`ml-auto h-4 w-4 text-[#ee2b74] transition-transform ${cakeExpanded ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {cakeExpanded && (
                        <div className="px-6 py-4 space-y-4">
                            {/* Meta row */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div>
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Collection Date</p>
                                    <div className="flex items-center gap-1.5">
                                        <CalendarIcon className="h-3.5 w-3.5 text-[#ee2b74]" />
                                        <p className="text-sm font-semibold text-stone-900">
                                            {new Date(cakeOrder!.collection_date).toLocaleDateString('en-ZA', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                            })}
                                        </p>
                                    </div>
                                </div>
                                {cakeOrder!.selection?.size_label && (
                                    <div>
                                        <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Size</p>
                                        <p className="text-sm font-semibold text-stone-900">{String(cakeOrder!.selection.size_label)}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Cake Total</p>
                                    <p className="text-sm font-semibold text-[#ee2b74]">{fmt(cakeOrder!.total_price)}</p>
                                </div>
                            </div>

                            {/* Cake message */}
                            {cakeOrder!.selection?.cake_message && (
                                <div className="rounded-lg bg-stone-50 border border-stone-200 px-4 py-3">
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Cake Message</p>
                                    <p className="text-sm text-stone-700 italic">"{String(cakeOrder!.selection.cake_message)}"</p>
                                </div>
                            )}

                            {/* Line items table */}
                            {cakeOrder!.line_items?.length > 0 && (
                                <div className="rounded-lg border border-stone-200 overflow-hidden">
                                    <table className="w-full text-sm">
                                        <thead className="bg-stone-50 border-b border-stone-200">
                                        <tr>
                                            <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Option</th>
                                            <th className="text-right px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Price</th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-stone-100">
                                        {cakeOrder!.line_items.map((item, i) => (
                                            <tr key={i} className="bg-white hover:bg-stone-50 transition">
                                                <td className="px-4 py-2.5 text-stone-700">{item.label}</td>
                                                <td className="px-4 py-2.5 text-right text-stone-500 tabular-nums">
                                                    {item.price > 0 ? fmt(item.price) : <span className="text-stone-300 text-xs">Included</span>}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                        <tfoot className="border-t border-stone-200 bg-stone-50">
                                        <tr>
                                            <td className="px-4 py-2.5 text-sm font-semibold text-stone-900">Total</td>
                                            <td className="px-4 py-2.5 text-right text-sm font-bold text-[#ee2b74]">{fmt(cakeOrder!.total_price)}</td>
                                        </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}

                            {/* Special notes */}
                            {cakeOrder!.special_notes && (
                                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
                                    <p className="text-[11px] text-amber-600 uppercase font-medium mb-1">Special Notes</p>
                                    <p className="text-sm text-amber-800">{cakeOrder!.special_notes}</p>
                                </div>
                            )}

                            {/* Collection status */}
                            {cakeOrder!.collected_at ? (
                                <div className="rounded-lg bg-[#f0f7e6] border border-[#c5e08a] px-4 py-3 flex items-start gap-2">
                                    <CheckCircleIcon className="h-4 w-4 text-[#5a7f28] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#5a7f28]">Cake collected</p>
                                        <p className="text-xs text-[#5a7f28]/80 mt-0.5">
                                            {new Date(cakeOrder!.collected_at).toLocaleString('en-ZA', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 flex items-start gap-2">
                                    <ShoppingBagIcon className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-xs text-sky-700">
                                        The cake must be collected in-store on the date above. It cannot be included in a delivery even if the rest of this order is being delivered.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            <EditItemModal
                isOpen={isModalOpen}
                item={selectedItem}
                onClose={() => { setIsModalOpen(false); setSelectedItem(null); }}
                onSave={handleModalSave}
                onDeleteTriggered={handleDeleteFromEdit}
                isSubmitting={isSubmitting}
            />

            <DeleteOrderItemModal
                isOpen={isDeleteModalOpen}
                item={selectedItem}
                initialReason={deleteReason}
                onClose={() => { setIsDeleteModalOpen(false); setSelectedItem(null); setDeleteReason(''); }}
                onConfirm={handleDeleteConfirm}
                isSubmitting={isSubmitting}
            />
        </>
    );
};

export default OrderDetailsTab;